import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../utils/api';
import { Clock, LogOut } from 'lucide-react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * Idle-timeout watchdog. Mounts once (App level), no-ops when logged out.
 * Shows a "session will expire" warning 2 minutes (configurable) before the
 * idle limit, then logs out and redirects with an explanatory banner.
 */
export const SessionSecurityManager = () => {
  const { user, logout } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const lastActivity = useRef(Date.now());
  const warningRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    apiCall('/api/security/policy').then(setPolicy).catch(() => {});
  }, [user]);

  const markActivity = useCallback(() => {
    // While the warning dialog is up, passive activity must NOT auto-extend —
    // the user has to explicitly click "Stay Logged In".
    if (!warningRef.current) lastActivity.current = Date.now();
  }, []);

  useEffect(() => {
    if (!user || !policy) return undefined;
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));
    const idleMs = policy.idle_timeout_min * 60 * 1000;
    const warnMs = (policy.warning_min || 2) * 60 * 1000;

    const tick = setInterval(() => {
      const elapsed = Date.now() - lastActivity.current;
      if (elapsed >= idleMs) {
        clearInterval(tick);
        apiCall('/api/security/log-timeout', { method: 'POST' }).catch(() => {});
        logout({ reason: 'idle' });
      } else if (elapsed >= idleMs - warnMs) {
        warningRef.current = true;
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.round((idleMs - elapsed) / 1000)));
      } else {
        warningRef.current = false;
        setWarning(false);
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
    };
  }, [user, policy, logout, markActivity]);

  const stayLoggedIn = () => {
    warningRef.current = false;
    lastActivity.current = Date.now();
    setWarning(false);
    apiCall('/api/security/policy').catch(() => {}); // touches session activity server-side
  };

  if (!user || !warning) return null;

  const mm = Math.floor(secondsLeft / 60);
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4" data-testid="idle-warning-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
          <Clock className="w-7 h-7 text-amber-500" />
        </div>
        <h3 className="text-lg font-bold text-slate-900">Are you still there?</h3>
        <p className="text-sm text-slate-500 mt-2">
          Your session will expire in <span className="font-semibold text-slate-900">{mm}:{ss}</span> due to inactivity.
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={stayLoggedIn}
            className="flex-1 h-11 rounded-full bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors"
            data-testid="stay-logged-in-btn"
          >
            Stay Logged In
          </button>
          <button
            onClick={() => logout({ reason: 'manual' })}
            className="flex-1 h-11 rounded-full border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
            data-testid="idle-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionSecurityManager;
